<template>
  <div>
    <div class="card">
      <div class="flex justify-content-between align-items-center gap-2">
        <h1 class="flex-none py-2">
          {{ currentQuoteNumber || t('newQuote') }}
        </h1>
        <Toolbar class="flex-grow-1 align-items-center gap-2">
          <template #end>
            <Button
              :label="t('createOrder')"
              icon="pi pi-shopping-cart"
              @click="onCreateOrder"
              :disabled="!canCreateOrder"
              :loading="isCreatingOrder"
            />
          </template>
        </Toolbar>
      </div>
      <Tabs :value="activeTab">
        <TabList>
          <Tab
            v-for="(tab, idx) in tabItems"
            :key="idx"
            :value="tab.key"
            @click="router.push(tab.route)"
          >
            <div class="flex align-items-center gap-2">
              <i :class="tab.icon" />
              <p class="capitalize">{{ tab.label }}</p>
            </div>
          </Tab>
        </TabList>
      </Tabs>
    </div>

    <div class="m-2">
      <RouterView />
    </div>
  </div>
</template>

<script lang="ts">
import { useQuoteLoader } from '@/domains/quotations/loaders'
export { useQuoteLoader }
</script>

<script setup lang="ts">
import { computed } from 'vue'
import { useRoute, type RouteLocationRaw } from 'vue-router'
import { useI18n } from 'vue-i18n'
import Toolbar from 'primevue/toolbar'
import { useToast } from 'primevue/usetoast'

import { useQuotation } from '@/domains/quotations/composables/useQuotation'
import { router } from '@/router'

definePage({
  name: 'QuotationDetailsLayout',
  redirect: (to) => {
    return {
      name: '/dashboard/quotations/[id]/(details)/general',
      params: { id: 'id' in to.params ? String(to.params.id) : '' }
    } satisfies RouteLocationRaw
  }
})

const { t } = useI18n()
const toast = useToast()
const { quotation, canCreateOrder, isCreatingOrder, toOrder } = useQuotation()
const route = useRoute()

const currentQuoteNumber = computed(() => quotation.value?.number)

const tabItems = computed<{ key: string; route: RouteLocationRaw; label: string; icon: string }[]>(
  () => {
    if (!quotation.value) return []

    return [
      {
        key: 'general',
        route: {
          name: '/dashboard/quotations/[id]/(details)/general',
          params: { id: quotation.value.id }
        } satisfies RouteLocationRaw,
        label: t('common.general'),
        icon: 'pi pi-info-circle'
      },
      {
        key: 'door-list',
        route: { name: 'door-list', params: { id: quotation.value.id } } satisfies RouteLocationRaw,
        label: t('doorList'),
        icon: 'pi pi-bars'
      }
    ]
  }
)

const activeTab = computed(() => {
  const match = tabItems.value.find((tab) => {
    const resolved = router.resolve(tab.route)
    return route.path.startsWith(resolved.path)
  })

  return match?.key ?? tabItems.value[0]?.key
})

const onCreateOrder = async () => {
  try {
    await toOrder()
    // TODO: navigate to Order instead of toast popup.
    toast.add({
      severity: 'success',
      summary: t('orderCreated'),
      life: 3000
    })
  } catch (error) {
    console.error('Error creating order:', error)
    toast.add({
      severity: 'error',
      summary: t('orderCreatedError'),
      life: 3000
    })
  }
}
</script>

<style lang="css" scoped>
.p-toolbar {
  border: none;
}
</style>

<i18n lang="yaml">
en:
  doorList: 'door list'
  newQuote: 'New Quote'
  createOrder: 'Create order'
  orderCreated: 'Order created'
  orderCreatedError: 'Error creating order.'
nl:
  doorList: 'deurlijst'
  newQuote: 'Nieuwe Offerte'
  createOrder: 'Order aanmaken'
  orderCreated: 'Order aangemaakt'
  orderCreatedError: 'Fout bij aanmakend order.'
de:
  doorList: 'türliste'
  newQuote: 'Neues Angebot'
  createOrder: 'Bestellung erstellen'
  orderCreated: 'Bestellung erstellt'
  orderCreatedError: 'Fehler beim Erstellen der Bestellung.'
</i18n>
